
import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from "@/components/ui/button";
import LocationHero from '@/components/locations/LocationHero';
import LocationFeatures from '@/components/locations/LocationFeatures';
import LocationTestimonials from '@/components/locations/LocationTestimonials';
import LocationCTA from '@/components/locations/LocationCTA';
import { Helmet } from 'react-helmet-async';
import { trackPhoneCallConversion } from '@/utils/trackPhoneCall';

const GentofeLocation = () => {
  const features = [
    {
      title: "I hjertet af Gentofte",
      description: "Undervisningslokaler i rolige og inspirerende omgivelser i Gentofte, med nem adgang til offentlig transport.",
      icon: "location"
    },
    {
      title: "Fleksible tidspunkter",
      description: "Undervisning tilpasset din hverdag - både hverdage og weekender efter aftale.",
      icon: "clock"
    },
    {
      title: "Alle aldersgrupper",
      description: "Specialiseret klaverundervisning for både børn, unge og voksne på alle niveauer.",
      icon: "music"
    },
    {
      title: "Professionel kvalitet",
      description: "Undervisning ved konservatorieuddannet pianist med omfattende erfaring som både udøvende musiker og underviser.",
      icon: "award"
    }
  ];

  const testimonials = [
    {
      name: "Henrik Petersen",
      location: "Gentofte",
      text: "Oscar har formået at vække min søns interesse for klassisk musik gennem sin engagerende og positive undervisningsstil. En fantastisk klaverlærer!",
      stars: 5
    },
    {
      name: "Louise Andersen",
      location: "Gentofte",
      text: "Som voksen elev har jeg oplevet stor fremgang under Oscars kyndige vejledning. Hans professionelle tilgang kombineret med en stor portion tålmodighed gør ham til en ideel lærer.",
      stars: 5
    },
    {
      name: "Jonas Mortensen",
      location: "Charlottenlund",
      text: "Oscar forstår at skabe et læringsmiljø, hvor man både bliver udfordret og motiveret. Hans passion for musik er inspirerende, og han formår at gøre hver time til en oplevelse.",
      stars: 5
    }
  ];

  return (
    <Layout>
      <Helmet>
        <title>Klaverundervisning i Gentofte | Få en gratis prøvetime!</title>
        <meta name="description" content="Professionel klaverundervisning i Gentofte med Oscar Gilbert. Book en gratis prøvetime og start din musikalske rejse i dag!" />
      </Helmet>
      
      <LocationHero 
        title="Klaverundervisning i Gentofte"
        description="Oplev inspirerende og professionel klaverundervisning med Oscar Gilbert i Gentofte. Undervisning på højt niveau for elever i alle aldre."
      />
      
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6">
                Klaverundervisning i <span className="text-piano-gold">Gentofte</span>
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                I Gentofte tilbyder Oscar Gilbert klaverundervisning i rolige og inspirerende omgivelser. Her kan du eller dit barn udforske klaveret og udvikle musikalske evner under kyndig vejledning.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Undervisningen foregår på et akustisk kvalitetsklaver og er tilpasset den enkelte elevs niveau, ambitioner og musikalske præferencer - uanset om du drømmer om at spille klassisk, jazz, pop eller filmmusik.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                Med en pædagogisk og struktureret tilgang skaber Oscar et positivt læringsmiljø, hvor teknisk udvikling og musikalsk forståelse går hånd i hånd med glæden ved at spille.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="bg-piano-gold hover:bg-piano-gold/90 text-white">
                  <Link to="/kontakt">Book Gratis Prøvetime</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/undervisning">Se Undervisningstilbud</Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-piano-gold to-amber-400 rounded-lg blur opacity-20"></div>
              <div className="relative bg-white p-3 rounded-lg shadow-xl">
                <div className="aspect-square bg-gray-100 rounded-md overflow-hidden">
                  <img 
                    src="/lovable-uploads/891ee3d3-fc7a-4f1b-925d-4c77de27f6ae.png" 
                    alt="Klaverundervisning i Gentofte" 
                    className="w-full h-full object-cover object-center object-[50%_35%]"
                    width="400"
                    height="400"
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <LocationFeatures features={features} />
      
      <LocationTestimonials testimonials={testimonials} locationName="Gentofte" />
      
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-serif font-bold mb-4">Find vej til undervisningen</h2>
              <p className="text-lg text-gray-600">
                Klaverundervisningen foregår på Niels Finsens Alle 86, 2860 Søborg, med nem adgang fra Gentofte.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-xl mb-10">
              <div className="overflow-hidden rounded-md">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12607.679143124358!2d12.514096630724325!3d55.734728901797794!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x465251f89eae19fd%3A0x1029a319978e54b7!2sNiels%20Finsens%20Alle%2086%2C%202860%20S%C3%B8borg!5e0!3m2!1sda!2sdk!4v1743668909093!5m2!1sda!2sdk" 
                  width="100%" 
                  height="450" 
                  style={{ border: 0 }} 
                  allowFullScreen={true} 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Lokation på kort">
                </iframe>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="text-xl font-serif font-semibold">Adresse</h3>
                  <p className="text-gray-700">
                    Niels Finsens Alle 86<br />
                    2860 Søborg
                  </p>
                  <div className="space-y-2">
                    <p className="text-gray-700"><strong>Transport fra Gentofte:</strong></p>
                    <p className="text-gray-700">• God forbindelse med bus fra Gentofte</p>
                    <p className="text-gray-700">• 15 minutters kørsel i bil</p>
                    <p className="text-gray-700">• Gratis parkering ved undervisningslokalet</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="text-xl font-serif font-semibold">Kontakt</h3>
                  <p className="text-gray-700">
                    Telefon: <a href="tel:+4540817127" className="text-piano-gold hover:underline" onClick={trackPhoneCallConversion}>+45 40 81 71 27</a>
                  </p>
                  <p className="text-gray-700">
                    Email: <a href="mailto:kontakt@pianoundervisning.dk" className="text-piano-gold hover:underline">kontakt@pianoundervisning.dk</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <LocationCTA locationName="Gentofte" />
    </Layout>
  );
};

export default GentofeLocation;
